/**
 * F-14A Tomcat Brick System Landing
 * Scroll reveals, counters, swing-wing, size compare, perspectives,
 * vitrine, sticky rails, add-to-cart. No Motion One dependency.
 */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE_OUT_CUBIC = (t) => 1 - Math.pow(1 - t, 3);

  function boot() {
    if (!document.querySelector('.tomcat-lp')) return;

    if (!REDUCED) {
      document.documentElement.classList.add('tomcat-js-ready');
    }

    initReveals();
    initCountUp();
    initHeroParallax();
    initSwingWing();
    initSizeCompare();
    initPerspectives();
    initVitrine();
    initFaq();
    initStickyChrome();
    initAddToCart();
  }

  /* ---------- Shared rAF scroll ---------- */
  function onScroll(fn) {
    let ticking = false;
    const run = () => {
      ticking = false;
      fn();
    };
    const onScrollHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(run);
    };
    window.addEventListener('scroll', onScrollHandler, { passive: true });
    window.addEventListener('resize', onScrollHandler, { passive: true });
    run();
    return () => {
      window.removeEventListener('scroll', onScrollHandler);
      window.removeEventListener('resize', onScrollHandler);
    };
  }

  /* ---------- Reveals ---------- */
  function initReveals() {
    const nodes = [...document.querySelectorAll('[data-tomcat-reveal]')];
    if (!nodes.length) return;

    if (REDUCED) {
      nodes.forEach((el) => el.classList.add('is-in'));
      return;
    }

    onScroll(() => {
      const limit = window.innerHeight * 0.92;
      nodes.forEach((el) => {
        if (el.classList.contains('is-in')) return;
        if (el.getBoundingClientRect().top < limit) {
          el.classList.add('is-in');
        }
      });
    });
  }

  /* ---------- Count-up ---------- */
  function initCountUp() {
    const nodes = [...document.querySelectorAll('[data-tomcat-count]')];
    if (!nodes.length) return;

    const run = (el) => {
      if (el.dataset.counted === '1') return;
      el.dataset.counted = '1';
      const target = parseFloat(el.dataset.tomcatCount);
      if (Number.isNaN(target)) return;
      const prefix = el.dataset.prefix || '';
      const suffix = el.dataset.suffix || '';
      const isInt = Number.isInteger(target);

      if (REDUCED) {
        el.textContent = prefix + (isInt ? target.toLocaleString('de-DE') : String(target)) + suffix;
        return;
      }

      const duration = 1100;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const v = target * EASE_OUT_CUBIC(t);
        const shown = isInt ? Math.round(v).toLocaleString('de-DE') : v.toFixed(0);
        el.textContent = prefix + shown + suffix;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(run);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            run(en.target);
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    nodes.forEach((n) => io.observe(n));
  }

  /* ---------- Hero parallax ---------- */
  function initHeroParallax() {
    if (REDUCED) return;
    const product = document.querySelector('[data-tomcat-parallax]');
    if (!product) return;
    const factor = parseFloat(product.dataset.tomcatParallax) || 0.05;

    onScroll(() => {
      const hero = product.closest('.tomcat-hero');
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      const progress = (window.innerHeight * 0.5 - (rect.top + rect.height * 0.5)) * factor;
      product.style.transform = `translate3d(0, ${progress.toFixed(2)}px, 0)`;
    });
  }

  /* ---------- Swing wing ---------- */
  function initSwingWing() {
    document.querySelectorAll('[data-tomcat-swing]').forEach((root) => {
      const input = root.querySelector('input[type="range"]');
      const degEl = root.querySelector('[data-swing-deg]');
      const modeEl = root.querySelector('[data-swing-mode]');
      const left = root.querySelector('[data-wing-left]');
      const right = root.querySelector('[data-wing-right]');
      if (!input) return;

      const apply = (raw) => {
        const t = Math.max(0, Math.min(100, Number(raw))) / 100;
        const deg = Math.round(8 + (55 - 8) * t);
        if (degEl) degEl.textContent = `${deg}°`;
        if (modeEl) {
          modeEl.textContent = deg < 32 ? 'Trägerlandung' : 'Überschall';
        }
        // Visual sweep: left wing swings clockwise, right counter
        const visual = (deg - 8) * 0.55;
        if (left) left.style.transform = `rotate(${visual}deg)`;
        if (right) right.style.transform = `rotate(${-visual}deg)`;
      };

      input.addEventListener('input', () => apply(input.value));
      apply(input.value || input.defaultValue || 0);
    });
  }

  /* ---------- Size compare ---------- */
  function initSizeCompare() {
    document.querySelectorAll('[data-tomcat-size]').forEach((root) => {
      const productCm = parseFloat(root.dataset.productCm) || 60;
      const maxCm = parseFloat(root.dataset.maxCm) || 200;
      const productFill = root.querySelector('[data-size-product]');
      const refFill = root.querySelector('[data-size-ref]');
      const refLabel = root.querySelector('[data-size-ref-label]');
      const refValue = root.querySelector('[data-size-ref-value]');
      const fazit = root.querySelector('[data-size-fazit]');
      const buttons = [...root.querySelectorAll('[data-size-ref-btn]')];

      const setScale = (el, cm) => {
        if (!el) return;
        el.style.setProperty('--scale', String(Math.max(0.02, Math.min(1, cm / maxCm))));
        el.classList.add('is-ready');
      };

      const select = (btn) => {
        const cm = parseFloat(btn.dataset.cm);
        const label = btn.dataset.label || btn.textContent.trim();
        buttons.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
        setScale(productFill, productCm);
        setScale(refFill, cm);
        if (refLabel) refLabel.textContent = label;
        if (refValue) refValue.textContent = `${cm} cm`;
        if (fazit) {
          if (cm > productCm) {
            const ratio = (cm / productCm).toFixed(1).replace('.', ',');
            fazit.textContent = `Dein Bezug (${label}) ist etwa ${ratio}× so lang wie die ${productCm} cm Spannweite des Tomcat.`;
          } else {
            const ratio = (productCm / cm).toFixed(1).replace('.', ',');
            fazit.textContent = `Die ${productCm} cm Spannweite überragt ${label} um etwa den Faktor ${ratio}.`;
          }
        }
      };

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => select(btn));
      });

      const initial = buttons.find((b) => b.getAttribute('aria-pressed') === 'true') || buttons[0];
      if (initial) {
        if (REDUCED) {
          select(initial);
        } else if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(
            (entries) => {
              entries.forEach((en) => {
                if (en.isIntersecting) {
                  select(initial);
                  io.disconnect();
                }
              });
            },
            { threshold: 0.3 }
          );
          io.observe(root);
        } else {
          select(initial);
        }
      }
    });
  }

  /* ---------- Perspectives ---------- */
  function initPerspectives() {
    document.querySelectorAll('[data-tomcat-persp]').forEach((root) => {
      const input = root.querySelector('input[type="range"]');
      const imgs = [...root.querySelectorAll('[data-persp-img]')];
      const label = root.querySelector('[data-persp-label]');
      if (!input || !imgs.length) return;

      const apply = (raw) => {
        const idx = Math.max(0, Math.min(imgs.length - 1, Math.round(Number(raw))));
        imgs.forEach((img, i) => img.classList.toggle('is-active', i === idx));
        if (label) label.textContent = `Ansicht ${idx + 1} / ${imgs.length}`;
        input.setAttribute('aria-valuenow', String(idx + 1));
        input.setAttribute('aria-valuetext', `Ansicht ${idx + 1} von ${imgs.length}`);
      };

      input.max = String(imgs.length - 1);
      input.addEventListener('input', () => apply(input.value));
      apply(input.value || 0);
    });
  }

  /* ---------- Vitrine ---------- */
  function initVitrine() {
    document.querySelectorAll('[data-tomcat-vitrine]').forEach((root) => {
      const stage = root.querySelector('[data-vitrine-stage]');
      const hint = root.querySelector('[data-vitrine-hint]');
      const product = root.querySelector('[data-vitrine-product]');
      const sceneImgs = [...root.querySelectorAll('[data-vitrine-scene-img]')];
      const buttons = [...root.querySelectorAll('[data-vitrine-btn]')];
      if (!stage || !buttons.length) return;

      const select = (btn) => {
        const env = btn.dataset.vitrineBtn;
        const hintText = btn.dataset.hint || '';
        const hasScene = btn.dataset.hasScene === 'true';
        buttons.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
        stage.dataset.env = env;
        stage.classList.toggle('has-bg', hasScene);
        if (hint) hint.textContent = hintText;
        sceneImgs.forEach((img) => {
          img.classList.toggle('is-active', img.dataset.vitrineSceneImg === env);
        });
        if (product) {
          if (env === 'regal') product.style.transform = 'scale(0.92) translateY(6%)';
          else if (env === 'schreibtisch') product.style.transform = 'scale(1) translateY(0)';
          else product.style.transform = 'scale(0.86) translateY(10%)';
        }
      };

      buttons.forEach((btn) => btn.addEventListener('click', () => select(btn)));
      const initial = buttons.find((b) => b.getAttribute('aria-pressed') === 'true') || buttons[0];
      if (initial) select(initial);
    });
  }

  /* ---------- FAQ ---------- */
  function initFaq() {
    document.querySelectorAll('[data-tomcat-faq]').forEach((list) => {
      list.querySelectorAll('.tomcat-faq__item').forEach((item) => {
        const btn = item.querySelector('.tomcat-faq__q');
        if (!btn) return;
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', () => {
          const open = item.classList.contains('is-open');
          list.querySelectorAll('.tomcat-faq__item').forEach((it) => {
            it.classList.remove('is-open');
            const q = it.querySelector('.tomcat-faq__q');
            if (q) q.setAttribute('aria-expanded', 'false');
          });
          if (!open) {
            item.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ---------- Sticky rail + bottom bar ---------- */
  function initStickyChrome() {
    const rail = document.querySelector('[data-tomcat-rail]');
    const sticky = document.querySelector('[data-tomcat-sticky]');
    const buy = document.querySelector('#kaufen');
    if (!rail && !sticky) return;

    onScroll(() => {
      const pastHero = window.scrollY > window.innerHeight * 0.9;
      let buyVisible = false;
      if (buy) {
        const r = buy.getBoundingClientRect();
        buyVisible = r.top < window.innerHeight * 0.75 && r.bottom > 0;
      }
      const show = pastHero && !buyVisible;
      if (rail) {
        rail.classList.toggle('is-visible', show);
        rail.setAttribute('aria-hidden', show ? 'false' : 'true');
      }
      if (sticky) {
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      }
    });
  }

  /* ---------- Add to cart ---------- */
  function initAddToCart() {
    document.querySelectorAll('[data-tomcat-product-form]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('button[type="submit"]');
        if (!btn || btn.disabled) return;
        const original = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = '<span>…</span>';
        try {
          const res = await fetch(window.themeVariables?.routes?.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            body: new FormData(form),
          });
          if (!res.ok) throw new Error('add failed');
          const cartRes = await fetch(window.themeVariables?.routes?.cartUrl || '/cart.js');
          const cart = await cartRes.json();
          const cartType = window.themeVariables?.settings?.cartType;
          const openDrawer = cartType === 'drawer';
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: openDrawer } })
          );
          btn.innerHTML = '<span>Hinzugefügt</span>';
          if (openDrawer) {
            const toggle = document.querySelector('[data-action="toggle-drawer"][data-drawer="cart"]');
            if (toggle && !toggle.classList.contains('is-active')) setTimeout(() => toggle.click(), 120);
          }
          setTimeout(() => {
            btn.disabled = false;
            btn.innerHTML = original;
          }, 2200);
        } catch (err) {
          console.error('[tomcat] add to cart', err);
          btn.disabled = false;
          btn.innerHTML = original;
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
